import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { getToasterConfig } from './constants/toastConstants';
import { AuthProvider } from './contexts/AuthContext';
import { WebSocketProvider, useWebSockets } from './contexts/WebSocketProvider';
import { SoundSettingsProvider } from './contexts/SoundSettingsContext';
import { NotificationProvider, useNotifications } from './contexts/NotificationContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import BrowserCompatibilityWarning from './components/common/BrowserCompatibilityWarning';
import InactiveChatReminder from './components/common/InactiveChatReminder';
import ReviewConversationModal from './components/common/ReviewConversationModal';
import AlertNeedsAttentionModal from './components/common/AlertNeedsAttentionModal';
import FollowUpRealtimeListener from './components/followUps/FollowUpRealtimeListener';
import { routes } from './routes';
import { forceUnlockBodyScroll } from './utils/scrollUtils';
import { requestNotificationPermission, createBrowserNotification } from './services/webNotificationService';
import { queryClient } from './lib/api/queryClient';
import { setNavigateFunction } from './lib/api/client';
import { useUserWebSocket } from './components/chat/hooks/useUserWebSocket';
import { incrementSpamMessage, markSpamConversation, removeSpamConversation, clearSpamConversationMessages } from './components/chat/utils/spamCounterStore';

// Import browser test in development
if (process.env.NODE_ENV === 'development') {
  import('./utils/browserTest');
}

// Component to trace WebSocket events and notify users
const WebSocketEventTracer = () => {
  const SOCKET_NEW_CONVERSATION_TABS_STORAGE_KEY = 'socket_new_conversation_tabs_state';
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantWebSocketService, userWebSocketService } = useWebSockets();
  const {
    incrementChatNotification,
    clearChatNotifications,
    incrementInternalChatNotification,
    clearInternalChatNotifications,
    incrementConversationUpdateCount,
    incrementTabUpdateCount,
  } = useNotifications();
  const internalInboxDedupRef = useRef({ id: null, at: 0 });
  const internalNewMessageDedupRef = useRef({ key: null, at: 0 });
  let hasInternalChatNotifications = false;

  const trackConversationForTabIndicators = (conversationId) => {
    const numericId = Number(conversationId);
    if (!numericId) return;
    try {
      const raw = localStorage.getItem(SOCKET_NEW_CONVERSATION_TABS_STORAGE_KEY);
      const previous = raw ? JSON.parse(raw) : {};
      const next = {
        All: Array.isArray(previous?.All) ? previous.All : [],
        Inbox: Array.isArray(previous?.Inbox) ? previous.Inbox : [],
        My: Array.isArray(previous?.My) ? previous.My : [],
        Alerts: Array.isArray(previous?.Alerts) ? previous.Alerts : [],
      };

      Object.keys(next).forEach((tab) => {
        if (!next[tab].includes(numericId)) {
          next[tab] = [...next[tab], numericId];
        }
      });

      localStorage.setItem(SOCKET_NEW_CONVERSATION_TABS_STORAGE_KEY, JSON.stringify(next));
    } catch (error) {
      console.warn('Failed to persist sidebar-triggered conversation indicator:', error);
    }
  };

  useEffect(() => {
    // Check if user is on chat page
    const isOnChatPage = () => window.location.pathname === '/chat';

    // Play notification sound
    const playNotificationSound = () => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(1000, audioContext.currentTime + 0.1);
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
        
        oscillator.type = 'sine';
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.2);
      } catch (error) {
        console.log('Could not play notification sound:', error);
      }
    };

    // Show browser notification
    const showBrowserNotification = (title, body, conversationId = null) => {
      if (!isOnChatPage()) {
        createBrowserNotification({
          title,
          body,
          icon: '/images/supbotive logo.png',
          data: { conversationId },
          onClick: () => {
            navigate('/chat');
            if (conversationId) {
              // Give the Chat page a moment to mount and register its
              // 'navigate_to_conversation' listener before dispatching.
              setTimeout(() => {
                window.dispatchEvent(
                  new CustomEvent('navigate_to_conversation', {
                    detail: { conversationId: Number(conversationId) },
                  })
                );
              }, 300);
            }
          }
        });
      }
    };

    // Show toast notification
    const showToast = (message, type = 'info') => {
      if (!isOnChatPage()) {
        const options = {
          duration: 4000,
          position: 'top-right',
        };

        switch (type) {
          case 'success':
            toast.success(message, options);
            break;
          case 'error':
            toast.error(message, options);
            break;
          default:
            toast(message, options);
        }
      }
    };

    // User WebSocket event listeners with notifications
    const userEvents = {
      'connected': { notify: false },
      'disconnected': { notify: false },
      'connecting': { notify: false },
      'error': { notify: false },
      'user_notification': { 
        notify: true, 
        title: 'New Notification',
        getMessage: (data) => data.message || 'You have a new notification',
        toast: 'You have a new notification'
      },
      'user_alert': { 
        notify: true,
        title: 'User Alert',
        getMessage: (data) => data.message || 'You have a new alert'
      },
      'user_status_update': { notify: false },
      'user_permission_update': { notify: false },
      'user_profile_update': { notify: false },
      'user_assignment_update': { notify: false },
      'user_online_status': { notify: false },
      'user_activity_update': { notify: false },
      'conversation.assigned': { 
        notify: true,
        title: 'New Conversation Assigned',
        getMessage: () => 'A new conversation has been assigned to you',
        toast: 'New conversation assigned to you'
      },
      'assignment.pending': { notify: false },
      'message_status_updated': { notify: false },
      'conversation.updated': { 
        notify: true,
        title: 'Conversation Updated',
        getMessage: () => 'A conversation has been updated'
      },
      'conversation.accepted': { notify: false },
      'transfer.accept': { 
        notify: true,
        title: 'Transfer Accepted',
        getMessage: () => 'A conversation transfer has been accepted',
        toast: 'Transfer accepted',
        toastType: 'success'
      },
      'transfer.reject': { notify: false },
      'chat_transferred': { 
        notify: true,
        title: 'Chat Transferred',
        getMessage: (data) => `${data.transferred_by_name || 'Someone'} transferred a chat to ${data.transferred_to_name || 'you'}`,
        toast: 'Chat transferred to you'
      },
      'token_refresh_failed': { notify: false },
      'max_reconnect_attempts_reached': { notify: false },
      'unknown': { notify: false }
    };

    // Tenant WebSocket event listeners with notifications
    const tenantEvents = {
      // Spam events ('Mark Chat as Spam' story) — show the Contact Center sidebar indicator
      // when the user is not on the /chat screen (same as other notify:true tenant events).
      'message.new.spam': {
        notify: true,
        title: 'New Spam Message',
        getMessage: () => 'A new spam message was received'
      },
      'conversation.marked_spam': {
        notify: true,
        title: 'Conversation Marked as Spam',
        getMessage: () => 'A conversation has been marked as spam'
      },
      'conversation.removed_from_spam': {
        notify: true,
        title: 'Conversation Removed from Spam',
        getMessage: () => 'A conversation has been removed from spam'
      },
      'connected': { notify: false },
      'disconnected': { notify: false },
      'connecting': { notify: false },
      'error': { notify: false },
      'chat_status_changed': { 
        notify: true,
        title: 'Chat Status Changed',
        getMessage: (data) => `Chat status changed to ${data.status || 'new status'}`
      },
      'chat_accepted': { notify: false },
      'chat_resolved': { notify: false },
      'chat_alert': { 
        notify: true,
        title: 'Chat Alert',
        getMessage: () => 'You have a new chat alert',
        toast: 'New chat alert!'
      },
      'conversation_accepted': { 
        notify: true,
        title: 'Conversation Accepted',
        getMessage: () => 'A conversation has been accepted'
      },
      'conversation_moved_to_alerts': { notify: false },
      'conversation_updated': { 
        notify: true,
        title: 'Conversation Updated',
        getMessage: () => 'A conversation status has changed'
      },
      'message_status_updated': { notify: false },
      'user_online': { notify: false },
      'user_offline': { notify: false },
      'tenant_notification': { 
        notify: true,
        title: 'Tenant Notification',
        getMessage: (data) => data.message || 'You have a new tenant notification'
      },
      'system_announcement': { 
        notify: true,
        title: 'System Announcement',
        getMessage: (data) => data.message || 'New system announcement'
      },
      'token_refresh_failed': { notify: false },
      'max_reconnect_attempts_reached': { notify: false },
      'unknown': { notify: false }
    };

    // Create handler maps to store references for cleanup
    const userHandlers = {};
    const tenantHandlers = {};

    // Create and register user event handlers
    Object.keys(userEvents).forEach(eventName => {
      userHandlers[eventName] = (data) => {
        const eventConfig = userEvents[eventName];
        if (eventConfig.notify && !isOnChatPage()) {
          trackConversationForTabIndicators(data.id || data.conversation_id || data.chat_id);
          incrementChatNotification();
          playNotificationSound();
          showBrowserNotification(
            eventConfig.title,
            eventConfig.getMessage ? eventConfig.getMessage(data) : '',
            data.id || data.conversation_id
          );
          if (eventConfig.toast) {
            showToast(eventConfig.toast, eventConfig.toastType || 'info');
          }
        }
      };
      userWebSocketService.on(eventName, userHandlers[eventName]);
    });

    // Create and register tenant event handlers
    Object.keys(tenantEvents).forEach(eventName => {
      tenantHandlers[eventName] = (data) => {
        console.log(`🟢 TENANT WEBSOCKET EVENT [${eventName}]:`, data);
        
        const eventConfig = tenantEvents[eventName];
        if (eventConfig.notify && !isOnChatPage()) {
          trackConversationForTabIndicators(data.id || data.conversation_id || data.chat_id);
          incrementChatNotification();
          playNotificationSound();
          showBrowserNotification(
            eventConfig.title,
            eventConfig.getMessage ? eventConfig.getMessage(data) : '',
            data.id || data.conversation_id || data.chat_id
          );
          if (eventConfig.toast) {
            showToast(eventConfig.toast, eventConfig.toastType || 'info');
          }
        }
      };
      tenantWebSocketService.on(eventName, tenantHandlers[eventName]);
    });

    console.log('🔍 WebSocket Event Tracer initialized - monitoring all events');
    console.log('📊 Monitoring', Object.keys(userEvents).length, 'user events and', Object.keys(tenantEvents).length, 'tenant events');

    return () => {
      // Cleanup: remove all event listeners using stored references
      Object.keys(userEvents).forEach(eventName => {
        if (userHandlers[eventName]) {
          userWebSocketService.off(eventName, userHandlers[eventName]);
        }
      });
      Object.keys(tenantEvents).forEach(eventName => {
        if (tenantHandlers[eventName]) {
          tenantWebSocketService.off(eventName, tenantHandlers[eventName]);
        }
      });
      console.log('🔍 WebSocket Event Tracer cleaned up');
    };
  }, [location, navigate, tenantWebSocketService, userWebSocketService, incrementChatNotification]);

  // Spam counters ('Mark Chat as Spam' story), maintained globally (regardless of current page)
  // so they are correct even when the user is not on /chat when an event arrives:
  //  - total spam conversations  → 'conversation.marked_spam' (+) / 'conversation.removed_from_spam' (-)
  //  - per-conversation message badge → 'message.new.spam' (+), cleared when the conversation is opened.
  useEffect(() => {
    if (!tenantWebSocketService) return;

    const resolveConversationId = (data) => {
      const message = data?.message && typeof data.message === 'object' ? data.message : null;
      const id =
        data?.conversation_id ??
        data?.conversation?.id ??
        message?.conversation ??
        data?.id;
      return id != null ? Number(id) : null;
    };

    // New spam message → increment that conversation's per-conversation badge (not the total)
    const onSpamMessageNew = (data) => incrementSpamMessage(resolveConversationId(data));

    // Conversation marked as spam → +1 total
    const onSpamMarked = (data) => markSpamConversation(resolveConversationId(data));

    // Conversation removed from spam → -1 total (and clear its badge)
    const onSpamRemovedFromSpam = (data) => removeSpamConversation(resolveConversationId(data));

    // A spam conversation was opened (read) → clear its new-message badge
    const onSpamConversationOpened = (event) => {
      const rawId = event?.detail?.conversationId;
      if (rawId != null) clearSpamConversationMessages(Number(rawId));
    };

    tenantWebSocketService.on('message.new.spam', onSpamMessageNew);
    tenantWebSocketService.on('conversation.marked_spam', onSpamMarked);
    tenantWebSocketService.on('conversation.removed_from_spam', onSpamRemovedFromSpam);
    window.addEventListener('spam_conversation_opened', onSpamConversationOpened);
    return () => {
      tenantWebSocketService.off('message.new.spam', onSpamMessageNew);
      tenantWebSocketService.off('conversation.marked_spam', onSpamMarked);
      tenantWebSocketService.off('conversation.removed_from_spam', onSpamRemovedFromSpam);
      window.removeEventListener('spam_conversation_opened', onSpamConversationOpened);
    };
  }, [tenantWebSocketService]);

  // Clear chat notifications when navigating to the chat page
  useEffect(() => {
    if (location.pathname === '/chat') {
      clearChatNotifications();
    }
  }, [location.pathname, clearChatNotifications]);

  useEffect(() => {
    if (location.pathname === '/internal-chat') {
      clearInternalChatNotifications();
    }
  }, [location.pathname, clearInternalChatNotifications]);

  return null;
};

// Component to handle route changes and reset scroll, and set up navigation
const RouteChangeHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Set up React Router navigation for API client logout
  useEffect(() => {
    setNavigateFunction(navigate);
  }, [navigate]);
  
  useEffect(() => {
    // Force unlock body scroll on route change
    // This catches cases where components unmount before cleanup runs
    forceUnlockBodyScroll();
    
    // Ensure window is at top and body is scrollable
    window.scrollTo(0, 0);
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
  }, [location.pathname]);
  
  return null;
};

// Keep user WebSocket active across all routes for global user events (e.g., role change)
const GlobalUserWebSocketListener = () => {
  useUserWebSocket();
  return null;
};

function App() {
  // Ensure body scroll is restored on app mount/unmount
  // This serves as a safety net if any component forgets to cleanup
  useEffect(() => {
    // Force unlock on mount (cleanup any stuck locks)
    forceUnlockBodyScroll();
    
    // Ensure body is scrollable by default
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
    
    // Cleanup on unmount
    return () => {
      forceUnlockBodyScroll();
    };
  }, []);
  
  // Ensure window doesn't have unwanted scroll position
  useEffect(() => {
    // If window is scrolled on mount, reset it
    if (window.scrollY !== 0) {
      window.scrollTo(0, 0);
    }
  }, []);

  // Initialize web notification permission on app load
  useEffect(() => {
    // Request notification permission after a short delay to avoid blocking initial render
    const timer = setTimeout(() => {
      requestNotificationPermission().catch((error) => {
        console.error('Error requesting notification permission:', error);
      });
    }, 2000); // 2 second delay

    return () => clearTimeout(timer);
  }, []);


  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <WebSocketProvider>
            <SoundSettingsProvider>
              <Router>
                <NotificationProvider>
                  <GlobalUserWebSocketListener />
                  <WebSocketEventTracer />
                  <RouteChangeHandler />
                  <BrowserCompatibilityWarning />
                  <InactiveChatReminder />
                  <ReviewConversationModal />
                  <AlertNeedsAttentionModal />
                  <FollowUpRealtimeListener />
                  <div className="min-h-screen bg-gray-50">
                    <Routes>
                      {routes.map((route, index) => (
                        <Route
                          key={index}
                          path={route.path}
                          element={route.element}
                        />
                      ))}
                    </Routes>
                  </div>
                  <Toaster {...getToasterConfig()} />
                </NotificationProvider>
              </Router>
            </SoundSettingsProvider>
          </WebSocketProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App; 